import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Divider,
  Box,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Collapse,
  Paper,
  Chip,
  Button,
  Tooltip,
  TextField
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
import { convertDateToWords } from "../utils/dateToWords";
import { useRef } from "react";

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

// Student Info Fields Configuration
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true },
  rollNo: { label: "Roll No", defaultVisible: false },
  name: { label: "Student's Name", defaultVisible: true },
  father: { label: "Father's Name", defaultVisible: true },
  mother: { label: "Mother's Name", defaultVisible: true },
  apaarId: { label: "Apaar ID", defaultVisible: true },
  aadhaarNumber: { label: "Aadhaar Number", defaultVisible: false },
  studied: { label: "Studied (Class)", defaultVisible: true },
  admissionNo: { label: "Admission No.", defaultVisible: true },
  casteCategory: { label: "Category/Caste", defaultVisible: false },
  standard: { label: "Standard", defaultVisible: false },
  dob: { label: "Date of Birth", defaultVisible: true },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false },
  heightWeight: { label: "Height/Weight", defaultVisible: false },
  contact: { label: "Contact Number", defaultVisible: false },
  address: { label: "Address", defaultVisible: false },
};

// Single Marksheet Component
const SingleMarksheet = ({ 
  student, 
  marksheet, 
  school, 
  themeColor, 
  isLandscape, 
  fieldVisibility,
  visibleFields 
}) => {
  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);
  
  const cellPadding = isLandscape ? "4px" : "8px";
  const thStyle = { border: `1px solid ${borderColor}`, padding: cellPadding, background: headerBg };
  const tdStyle = { border: `1px solid ${borderColor}`, padding: cellPadding };

  const fontSize = isLandscape ? "0.7rem" : "0.9rem";
  // const headerFontSize = isLandscape ? "0.8rem" : "1rem";

  const classRoom = marksheet?.classRoom || student?.marksheets?.[0]?.classRoom || {};

  // Get student info with current marksheet data
  const getStudentInfo = () => {
    if (!student) return {};
    
    return {
      apaarId: student.apaarId || '',
      aadhaarNumber: student.aadhaar || '',
      name: toCamelCase(`${student.name || ''} ${student.last || ''}`.trim()),
      father: toCamelCase(`${student.father || ''} ${student.last || ''}`.trim()),
      mother: toCamelCase(`${student.mother || ''} ${student.last || ''}`.trim()),
      studied: `${classRoom.standard || ''} | ${classRoom.section || ''}`,
      admissionNo: student.id || student.studentId || '',
      casteCategory: `${student.category || ''} | caste: ${student.caste || ''}`,
      dob: student.birthday ? new Date(student.birthday).toLocaleDateString("en-GB") : '',
      dobInWords: student.birthday ? convertDateToWords(student.birthday) : '',
      fullname: toCamelCase(student.studentName || ''),
      photo: student.photo || null,
      contact: student.mobile || '',
      address: toCamelCase(`${student.address || ''} ${student.city || ''} ${student.state || ''}`.trim()) || '',
      rollNo: student.rollNumber || '',
      examType: marksheet?.examType || '',
      academicYear: marksheet?.academicYear || classRoom.academicYear || '',
      standard: `${classRoom.standard || ''} | ${classRoom.section || ''}`,
      percentage: marksheet?.percentage || '',
      grade: marksheet?.grade || '',
      result: marksheet?.result || '',
      remarks: marksheet?.remarks || '',
    };
  };

  const studentInfo = getStudentInfo();
  const marks = marksheet?.marks || [];
  const totalMax = marks.reduce((sum, m) => sum + Number(m.maxMark || 0), 0);
  const totalObt = marks.reduce((sum, m) => sum + Number(m.marksObtained || 0), 0);

  // Render student info field
  const renderStudentField = (fieldKey) => {
    if (!fieldVisibility[fieldKey]) return null;

    const field = STUDENT_INFO_FIELDS[fieldKey];
    let value = studentInfo[fieldKey];

    if (fieldKey === 'photo') {
      if (!value) return null;
      return (
        <Box key={fieldKey} display="flex" justifyContent="center" my={1}>
          <img
            src={value}
            alt="Student"
            style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
            onError={(e) => e.target.style.display = 'none'}
          />
        </Box>
      );
    }

    if (fieldKey === 'heightWeight') {
      return (
        <Box key={fieldKey} display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" component="span" fontWeight={700} sx={{ fontSize: fontSize }}>{field.label}</Typography>
          <TextField
            variant="standard"
            placeholder="Height/Weight"
            value={value}
            size="small"
            slotProps={{
              input: {
                disableUnderline: true,
              },
            }}
          />
        </Box>
      );
    }

    if (!value || value === '') return null;

    return (
      <Box
        key={fieldKey}
        sx={{
          display: "grid",
          gridTemplateColumns: isLandscape ? "200px auto" : "180px auto",
          mb: 0.3,
          gap: 0.5,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: fontSize }} component="span">
          {field.label} :
        </Typography>
        <Typography sx={{ fontSize: fontSize }} component="span">
          {value}
        </Typography>
      </Box>
    );
  };

  // Get fields for column 1 and 2
  const column1Fields = visibleFields.filter(key => STUDENT_INFO_FIELDS[key]?.column !== 2 && key !== 'photo');
  const column2Fields = visibleFields.filter(key => STUDENT_INFO_FIELDS[key]?.column === 2 && key !== 'photo');

  return (
    <Box
      sx={{
        maxWidth: isLandscape ? "1100px" : "800px",
        margin: "auto",
        px: isLandscape ? 3 : 4,
        pb: isLandscape ? 3 : 4,
        pt: isLandscape ? 1.5 : 2,
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
        backgroundImage: `url(${school?.logoUrl?.replace(/\\/g, "/")})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "400px auto",
        backgroundBlendMode: "lighten",
        position: "relative",
      }}
    >
      {/* Header with Logo */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        flexDirection="column"
      >
        <img
          src={school?.logoUrl}
          alt="School Logo"
          style={{ width: isLandscape ? 70 : 90, height: isLandscape ? 60 : 80, marginTop: 5 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <Typography variant={isLandscape ? "h5" : "h5"} sx={{ fontWeight: "bold", color: borderColor }}>
          {school?.schoolName}
        </Typography>
        <Typography sx={{ pb: 1, textAlign: "center", fontSize: isLandscape ? "0.75rem" : "0.9rem" }}>
          {toCamelCase(
            [
              school?.addressDTO?.addressLine1,
              school?.addressDTO?.city,
              school?.addressDTO?.pincode,
              school?.addressDTO?.state,
            ]
              .filter(Boolean)
              .join(", ")
          )}
        </Typography>
        <Typography
          variant={isLandscape ? "subtitle1" : "h6"}
          sx={{
            bgcolor: headerBg,
            borderRadius: 8,
            fontWeight: "bold",
            px: 3,
            py: 0.5,
            borderBottom: `3px solid ${borderColor}`,
            borderLeft: `2px solid ${borderColor}`,
            borderRight: `1px solid ${borderColor}`,
            mb: 2,
            fontSize: isLandscape ? "0.85rem" : "1rem",
          }}
        >
          {"MARKS STATEMENT"}
        </Typography>
      </Box>

      {/* Photo - absolute positioned */}
      {/* {fieldVisibility.photo && studentInfo.photo && (
        <Box position={'absolute'} right={0} top={0}>
          <img
            src={studentInfo.photo}
            alt="Student"
            style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
            onError={(e) => e.target.style.display = 'none'}
          />
        </Box>
      )} */}

      <Grid container spacing={isLandscape ? 10 : 13} sx={{ mt: 1 }} flexWrap={'nowrap'}>
        {/* Column 1 */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {column1Fields.map(key => renderStudentField(key))}
          </Box>
        </Grid>

        {/* Column 2 */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {column2Fields.map(key => renderStudentField(key))}
          </Box>
        </Grid>

        {/* Column 3 */}
        <Grid item xs={12} sm={6}>
          {fieldVisibility.photo && studentInfo.photo && (
        <Box position={'absolute'} right={10}>
          <img
            src={studentInfo.photo}
            alt="Student"
            style={{ width: isLandscape ? 100 : 100, height: isLandscape ? 100 : 100, borderRadius: '5%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
            onError={(e) => e.target.style.display = 'none'}
          />
        </Box>
      )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2, mt: 2, borderColor }} />

      {/* Exam Type and Academic Year */}
      <Box display="flex" justifyContent="space-between" sx={{ mb: 2 }}>
        {fieldVisibility.examType && studentInfo.examType && (
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: isLandscape ? "0.85rem" : "1rem" }}>
            {toCamelCase(studentInfo.examType)}
          </Typography>
        )}
        {fieldVisibility.academicYear && studentInfo.academicYear && (
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: isLandscape ? "0.85rem" : "1rem" }}>
            Academic Year: {studentInfo.academicYear}
          </Typography>
        )}
      </Box>

      {/* Marks Table */}
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Subject</th>
              <th style={thStyle}>Marks Obtained</th>
              <th style={thStyle}>Max Marks</th>
              <th style={thStyle}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {marks.map((m, index) => (
              <tr key={m.id || index}>
                <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>{index + 1}</td>
                <td style={{ ...tdStyle, fontSize: fontSize }}>{toCamelCase(`${m.subjectName}`)}</td>
                <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>{m.marksObtained || "-"}</td>
                <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>{m.maxMark || "-"}</td>
                <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>{m.grade || "-"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: totalsBg, fontWeight: "bold" }}>
              <td colSpan="2" style={{ ...tdStyle, textAlign: "right", fontSize: fontSize }}>
                <b>Total</b>
              </td>
              <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>
                <b>{totalObt}</b>
              </td>
              <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>
                <b>{totalMax}</b>
              </td>
              <td style={{ ...tdStyle, textAlign: "center", fontSize: fontSize }}>
                <b>{marksheet?.grade || studentInfo.grade || "-"}</b>
              </td>
            </tr>
          </tfoot>
        </table>
      </Box>

      {/* Summary Section */}
      <Box sx={{ my: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontSize: fontSize }}>
          <strong>Total Marks:</strong> {totalObt}/{totalMax}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: fontSize }}>
          <strong>Percentage:</strong> {studentInfo.percentage || 'N/A'}%
        </Typography>
        <Typography variant="body2" sx={{ fontSize: fontSize }}>
          <strong>Grade:</strong> {studentInfo.grade || 'N/A'}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: fontSize }}>
          <strong>Result:</strong>{" "}
          <span style={{
            color: studentInfo.result === "PASS" ? "#0904ff" : "#f44336",
            fontWeight: "bold"
          }}>
            {studentInfo.result || 'N/A'}
          </span>
        </Typography>
      </Box>

      {fieldVisibility.remarks && studentInfo.remarks && (
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: fontSize }}>
          <b>Remarks: </b>
          {studentInfo.remarks}
        </Typography>
      )}

      {/* Signatures */}
      <Box mt={4} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="body2" sx={{ fontSize: fontSize }}>Parent's Signature: _______</Typography>
        <Typography variant="body2" sx={{ fontSize: fontSize }}>Class Teacher Sign.: _______</Typography>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: fontSize }}>
            Principal/HeadMaster
          </Typography>
          <Typography variant="body2" sx={{ fontSize: fontSize }}>Seal/Sign.: ________</Typography>
        </Box>
      </Box>

      {/* Watermark for status */}
      {studentInfo.result === "PASS" ? (
        <Box
          sx={{
            position: "absolute",
            bottom: 20,
            right: 20,
            opacity: 0.03,
            transform: "rotate(-15deg)",
            fontSize: 28,
            fontWeight: "bold",
            color: "#0904ff",
            pointerEvents: "none",
          }}
        >
          PASS
        </Box>
      ) : (
        <Box
          sx={{
            position: "absolute",
            bottom: 20,
            right: 20,
            opacity: 0.1,
            transform: "rotate(-15deg)",
            fontSize: 48,
            fontWeight: "bold",
            color: "#f44336",
            pointerEvents: "none",
          }}
        >
          FAIL
        </Box>
      )}
    </Box>
  );
};

const OneTermMarksheetReceiptBulk = ({ receiptData, school, setTemID }) => {
  const students = receiptData.data || [];
  const receiptId = `marksheet`;
  const hasFetched = useRef(false);

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('oneTermBulkMarksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (setTemID) {
      setTemID(receiptId);
    }
    console.log('Students data:', students);
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('oneTermBulkMarksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // Add landscape styles to document
  useEffect(() => {
    if (isLandscape) {
      const style = document.createElement('style');
      style.id = 'landscape-style-one-term-bulk';
      style.innerHTML = `
        @media print {
          @page {
            size: landscape;
            margin: 8mm;
          }
          .pagebreak {
            page-break-after: always;
          }
        }
      `;
      document.head.appendChild(style);
    } else {
      const existingStyle = document.getElementById('landscape-style-one-term-bulk');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
    return () => {
      const existingStyle = document.getElementById('landscape-style-one-term-bulk');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLandscape]);

  // Flatten all marksheets from all students
  const allMarksheets = useMemo(() => {
    const sheets = [];
    students.forEach(student => {
      if (student.marksheets && Array.isArray(student.marksheets)) {
        student.marksheets.forEach(ms => {
          sheets.push({
            ...ms,
            studentData: student,
          });
        });
      }
    });
    return sheets;
  }, [students]);

  // Discover exam types
  const availableTerms = useMemo(() => {
    const set = new Set(
      allMarksheets.map((s) => s.examType?.trim()).filter(Boolean)
    );
    return Array.from(set);
  }, [allMarksheets]);

  // Filter marksheets by selected term
  const filteredMarksheets = useMemo(() => {
    if (selectedTerm === "all") return allMarksheets;
    return allMarksheets.filter(
      (s) => s.examType?.trim().toLowerCase() === selectedTerm.trim().toLowerCase()
    );
  }, [allMarksheets, selectedTerm]);

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  // Toggle landscape mode
  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  const containerMaxWidth = isLandscape ? "1100px" : "800px";

  // Get visible fields
  const visibleFields = Object.keys(STUDENT_INFO_FIELDS)
    .filter(key => fieldVisibility[key]);

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: containerMaxWidth,
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem" component="span">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="term-filter-label" sx={{ fontSize: "0.8rem" }}>Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            sx={{ fontSize: "0.8rem" }}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {availableTerms.map((t) => (
              <MenuItem key={t} value={t}>
                {toCamelCase(t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
          <Chip
            size="small"
            label={`${visibleCount}/${totalFields}`}
            color="primary"
            variant="outlined"
            sx={{ height: 28 }}
          />
        </Tooltip>
      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: containerMaxWidth,
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem" component="span">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)} sx={{ fontSize: "0.75rem" }}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)} sx={{ fontSize: "0.75rem" }}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={0.5}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5} component="span">
                      <Typography variant="caption" fontSize="0.7rem" component="span">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Generate Marksheets */}
      {filteredMarksheets.map((ms, index) => {
        const student = ms.studentData;
        const isLast = index === filteredMarksheets.length - 1;

        return (
          <React.Fragment key={`${student?.id}-${ms.id}-${index}`}>
            <SingleMarksheet
              student={student}
              marksheet={ms}
              school={school}
              themeColor={themeColor}
              isLandscape={isLandscape}
              fieldVisibility={fieldVisibility}
              visibleFields={visibleFields}
            />
            {/* Page break between marksheets */}
            {!isLast && <div className="pagebreak" />}
          </React.Fragment>
        );
      })}

      {/* Empty state */}
      {filteredMarksheets.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="textSecondary">
            No marksheet data available for the selected term.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OneTermMarksheetReceiptBulk;